import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectKeyboardShortcutModalOpen } from 'modules/Editor/store/editorSettingsSelector';
import { setKeyboardShortcutModalOpen } from 'modules/Editor/store/editorSettingsSlice';
import { Keyboard } from 'modules/common/components/Keyboard';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getBrowserString } from 'utils/browser';

export const CopyPasteShortcutModal = memo(() => {
  const isOpen = useAppSelector(selectKeyboardShortcutModalOpen);
  const dispatch = useAppDispatch();

  const closeModal = useCallback(() => {
    dispatch(setKeyboardShortcutModalOpen(false));
  }, [dispatch]);

  return (
    <Modal isOpen={isOpen} onClose={closeModal} isCentered={true}>
      <ModalOverlay />
      <CopyPasteShortcutContent onClose={closeModal} />
    </Modal>
  );
});
CopyPasteShortcutModal.displayName = 'CopyPasteShortcutModal';

interface CopyPasteShortcutContentProps {
  onClose: () => void;
}

const CopyPasteShortcutContent = memo(({ onClose }: CopyPasteShortcutContentProps) => {
  const { t } = useTranslation('editor_modals', { useSuspense: false, keyPrefix: 'copyPasteShortcutModal' });
  const headerText = t('header');
  const bodyText = t('bodyText', { returnObjects: true, browser: getBrowserString('your browser') });

  const copyLabel = t('copyLabel');
  const cutLabel = t('cutLabel');
  const pasteLabel = t('pasteLabel');
  const buttonText = t('buttonText');

  return (
    <ModalContent w='xl' maxW='xl' color='font.500'>
      <ModalHeader fontSize='18px'>{headerText}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Text fontSize='sm' lineHeight='2'>
          {bodyText[0]} <br />
          {bodyText[1]}
        </Text>
        <Flex justifyContent='space-around' mt='4'>
          <KeyboardShortcuts macOs={['⌘', 'C']} shortcut={['Ctrl', 'C']} label={copyLabel} />
          <KeyboardShortcuts macOs={['⌘', 'X']} shortcut={['Ctrl', 'X']} label={cutLabel} />
          <KeyboardShortcuts macOs={['⌘', 'V']} shortcut={['Ctrl', 'V']} label={pasteLabel} />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button variant='action' size='sm' onClick={onClose}>
          {buttonText}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
});
CopyPasteShortcutContent.displayName = 'CopyPasteShortcutContent';

interface KeyboardShortcutsProps {
  macOs: string[];
  shortcut: string[];
  label: string;
}

const KeyboardShortcuts = ({ macOs, shortcut, label }: KeyboardShortcutsProps) => {
  const [macOsKey1, macOsKey2] = macOs;
  const [shortcutKey1, shortcutKey2] = shortcut;

  return (
    <Flex direction='column' justifyContent='center' alignItems='center' gap='1'>
      <Flex alignItems='center' gap='2'>
        <KeyboardKey macOsKey={macOsKey1} otherKey={shortcutKey1} />
        <Text>+</Text>
        <KeyboardKey macOsKey={macOsKey2} otherKey={shortcutKey2} />
      </Flex>
      <Text fontWeight='semibold' fontSize='xs'>
        {label}
      </Text>
    </Flex>
  );
};

interface KeyboardKeyProps {
  macOsKey: string;
  otherKey: string;
}

const KeyboardKey = ({ macOsKey, otherKey }: KeyboardKeyProps) => {
  return (
    <Keyboard
      macOs={[macOsKey]}
      shortcut={[otherKey]}
      fontSize='2xl'
      color='font.500'
      borderRadius='base'
      bgColor='gray.50'
      bg='gray.50'
      w={12}
      h={12}
      display='flex'
      alignItems='center'
      justifyContent='center'
      fontFamily='Roboto Mono, monospace'
    />
  );
};
